/*
 * [js-sha1]{@link https://github.com/emn178/js-sha1}
 *
 * @version 0.7.0
 * @author Chen, Yi-Cyuan [emn178@gmail.com]
 * @copyright Chen, Yi-Cyuan 2014-2024
 * @license MIT
 */
/* jslint bitwise: true */

var INPUT_ERROR = "input is invalid type";
var FINALIZE_ERROR = "finalize already called";
var WINDOW = typeof window === "object";
var root = WINDOW ? window : {};
if (root.JS_SHA1_NO_WINDOW) {
  WINDOW = false;
}
var WEB_WORKER = !WINDOW && typeof self === "object";
var NODE_JS = !root.JS_SHA1_NO_NODE_JS && typeof process === "object" && process.versions && process.versions.node;
if (NODE_JS) {
  root = global;
} else if (WEB_WORKER) {
  root = self;
}
var ARRAY_BUFFER = !root.JS_SHA1_NO_ARRAY_BUFFER && typeof ArrayBuffer !== "undefined";
var HEX_CHARS = "0123456789abcdef".split("");
var EXTRA = [-2147483648, 8388608, 32768, 128];
var SHIFT = [24, 16, 8, 0];
var OUTPUT_TYPES = ["hex", "array", "digest", "arrayBuffer"];

var blocks = [];

var isArray = Array.isArray;
if (root.JS_SHA1_NO_NODE_JS || !isArray) {
  isArray = function (obj) {
    return Object.prototype.toString.call(obj) === "[object Array]";
  };
}

var isView = ArrayBuffer.isView;
if (ARRAY_BUFFER && (root.JS_SHA1_NO_ARRAY_BUFFER_IS_VIEW || !isView)) {
  isView = function (obj) {
    return typeof obj === "object" && obj.buffer && obj.buffer.constructor === ArrayBuffer;
  };
}

// [message: string, isString: bool]
var formatMessage = function (message) {
  var type = typeof message;
  if (type === "string") {
    return [message, true];
  }
  if (type !== "object" || message === null) {
    throw new Error(INPUT_ERROR);
  }
  if (ARRAY_BUFFER && message.constructor === ArrayBuffer) {
    return [new Uint8Array(message), false];
  }
  if (!isArray(message) && !isView(message)) {
    throw new Error(INPUT_ERROR);
  }
  return [message, false];
};

var createOutputMethod = function (outputType) {
  return function (message) {
    return new Sha1(true).update(message)[outputType]();
  };
};

var createMethod = function () {
  var method = createOutputMethod("hex");
  if (NODE_JS) {
    method = nodeWrap(method);
  }
  method.create = function () {
    return new Sha1();
  };
  method.update = function (message) {
    return method.create().update(message);
  };
  for (var i = 0; i < OUTPUT_TYPES.length; ++i) {
    var type = OUTPUT_TYPES[i];
    method[type] = createOutputMethod(type);
  }
  return method;
};

var nodeWrap = function (method) {
  var crypto = require("crypto");
  var Buffer = require("buffer").Buffer;
  var bufferFrom;
  if (Buffer.from && !root.JS_SHA1_NO_BUFFER_FROM) {
    bufferFrom = Buffer.from;
  } else {
    bufferFrom = function (message) {
      return new Buffer(message);
    };
  }
  var nodeMethod = function (message) {
    if (typeof message === "string") {
      return crypto.createHash("sha1").update(message, "utf8").digest("hex");
    } else {
      if (message === null || message === undefined) {
        throw new Error(INPUT_ERROR);
      } else if (message.constructor === ArrayBuffer) {
        message = new Uint8Array(message);
      }
    }
    if (isArray(message) || isView(message) || message.constructor === Buffer) {
      return crypto.createHash("sha1").update(bufferFrom(message)).digest("hex");
    } else {
      return method(message);
    }
  };
  return nodeMethod;
};

var createHmacOutputMethod = function (outputType) {
  return function (key, message) {
    return new HmacSha1(key, true).update(message)[outputType]();
  };
};

var createHmacMethod = function () {
  var method = createHmacOutputMethod("hex");
  method.create = function (key) {
    return new HmacSha1(key);
  };
  method.update = function (key, message) {
    return method.create(key).update(message);
  };
  for (var i = 0; i < OUTPUT_TYPES.length; ++i) {
    var type = OUTPUT_TYPES[i];
    method[type] = createHmacOutputMethod(type);
  }
  return method;
};

function Sha1(sharedMemory) {
  if (sharedMemory) {
    blocks[0] =
      blocks[16] =
      blocks[1] =
      blocks[2] =
      blocks[3] =
      blocks[4] =
      blocks[5] =
      blocks[6] =
      blocks[7] =
      blocks[8] =
      blocks[9] =
      blocks[10] =
      blocks[11] =
      blocks[12] =
      blocks[13] =
      blocks[14] =
      blocks[15] =
        0;
    this.blocks = blocks;
  } else {
    this.blocks = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
  }

  this.h0 = 0x67452301;
  this.h1 = 0xefcdab89;
  this.h2 = 0x98badcfe;
  this.h3 = 0x10325476;
  this.h4 = 0xc3d2e1f0;

  this.block = this.start = this.bytes = this.hBytes = 0;
  this.finalized = this.hashed = false;
  this.first = true;
}

Sha1.prototype.update = function (message) {
  if (this.finalized) {
    throw new Error(FINALIZE_ERROR);
  }

  var result = formatMessage(message);
  message = result[0];
  var isString = result[1];
  var code,
    index = 0,
    i,
    length = message.length || 0,
    blocks = this.blocks;

  while (index < length) {
    if (this.hashed) {
      this.hashed = false;
      blocks[0] = this.block;
      this.block =
        blocks[16] =
        blocks[1] =
        blocks[2] =
        blocks[3] =
        blocks[4] =
        blocks[5] =
        blocks[6] =
        blocks[7] =
        blocks[8] =
        blocks[9] =
        blocks[10] =
        blocks[11] =
        blocks[12] =
        blocks[13] =
        blocks[14] =
        blocks[15] =
          0;
    }

    if (isString) {
      for (i = this.start; index < length && i < 64; ++index) {
        code = message.charCodeAt(index);
        if (code < 0x80) {
          blocks[i >>> 2] |= code << SHIFT[i++ & 3];
        } else if (code < 0x800) {
          blocks[i >>> 2] |= (0xc0 | (code >>> 6)) << SHIFT[i++ & 3];
          blocks[i >>> 2] |= (0x80 | (code & 0x3f)) << SHIFT[i++ & 3];
        } else if (code < 0xd800 || code >= 0xe000) {
          blocks[i >>> 2] |= (0xe0 | (code >>> 12)) << SHIFT[i++ & 3];
          blocks[i >>> 2] |= (0x80 | ((code >>> 6) & 0x3f)) << SHIFT[i++ & 3];
          blocks[i >>> 2] |= (0x80 | (code & 0x3f)) << SHIFT[i++ & 3];
        } else {
          code = 0x10000 + (((code & 0x3ff) << 10) | (message.charCodeAt(++index) & 0x3ff));
          blocks[i >>> 2] |= (0xf0 | (code >>> 18)) << SHIFT[i++ & 3];
          blocks[i >>> 2] |= (0x80 | ((code >>> 12) & 0x3f)) << SHIFT[i++ & 3];
          blocks[i >>> 2] |= (0x80 | ((code >>> 6) & 0x3f)) << SHIFT[i++ & 3];
          blocks[i >>> 2] |= (0x80 | (code & 0x3f)) << SHIFT[i++ & 3];
        }
      }
    } else {
      for (i = this.start; index < length && i < 64; ++index) {
        blocks[i >>> 2] |= message[index] << SHIFT[i++ & 3];
      }
    }

    this.lastByteIndex = i;
    this.bytes += i - this.start;
    if (i >= 64) {
      this.block = blocks[16];
      this.start = i - 64;
      this.hash();
      this.hashed = true;
    } else {
      this.start = i;
    }
  }
  if (this.bytes > 4294967295) {
    this.hBytes += (this.bytes / 4294967296) << 0;
    this.bytes = this.bytes % 4294967296;
  }
  return this;
};

Sha1.prototype.finalize = function () {
  if (this.finalized) {
    return;
  }
  this.finalized = true;
  var blocks = this.blocks,
    i = this.lastByteIndex;
  blocks[16] = this.block;
  blocks[i >>> 2] |= EXTRA[i & 3];
  this.block = blocks[16];
  if (i >= 56) {
    if (!this.hashed) {
      this.hash();
    }
    blocks[0] = this.block;
    blocks[16] =
      blocks[1] =
      blocks[2] =
      blocks[3] =
      blocks[4] =
      blocks[5] =
      blocks[6] =
      blocks[7] =
      blocks[8] =
      blocks[9] =
      blocks[10] =
      blocks[11] =
      blocks[12] =
      blocks[13] =
      blocks[14] =
      blocks[15] =
        0;
  }
  blocks[14] = (this.hBytes << 3) | (this.bytes >>> 29);
  blocks[15] = this.bytes << 3;
  this.hash();
};

Sha1.prototype.hash = function () {
  var a = this.h0,
    b = this.h1,
    c = this.h2,
    d = this.h3,
    e = this.h4;
  var f,
    j,
    t,
    blocks = this.blocks;

  for (j = 16; j < 80; ++j) {
    t = blocks[j - 3] ^ blocks[j - 8] ^ blocks[j - 14] ^ blocks[j - 16];
    blocks[j] = (t << 1) | (t >>> 31);
  }

  for (j = 0; j < 20; j += 5) {
    f = (b & c) | (~b & d);
    t = (a << 5) | (a >>> 27);
    e = (t + f + e + 1518500249 + blocks[j]) << 0;
    b = (b << 30) | (b >>> 2);

    f = (a & b) | (~a & c);
    t = (e << 5) | (e >>> 27);
    d = (t + f + d + 1518500249 + blocks[j + 1]) << 0;
    a = (a << 30) | (a >>> 2);

    f = (e & a) | (~e & b);
    t = (d << 5) | (d >>> 27);
    c = (t + f + c + 1518500249 + blocks[j + 2]) << 0;
    e = (e << 30) | (e >>> 2);

    f = (d & e) | (~d & a);
    t = (c << 5) | (c >>> 27);
    b = (t + f + b + 1518500249 + blocks[j + 3]) << 0;
    d = (d << 30) | (d >>> 2);

    f = (c & d) | (~c & e);
    t = (b << 5) | (b >>> 27);
    a = (t + f + a + 1518500249 + blocks[j + 4]) << 0;
    c = (c << 30) | (c >>> 2);
  }

  for (; j < 40; j += 5) {
    f = b ^ c ^ d;
    t = (a << 5) | (a >>> 27);
    e = (t + f + e + 1859775393 + blocks[j]) << 0;
    b = (b << 30) | (b >>> 2);

    f = a ^ b ^ c;
    t = (e << 5) | (e >>> 27);
    d = (t + f + d + 1859775393 + blocks[j + 1]) << 0;
    a = (a << 30) | (a >>> 2);

    f = e ^ a ^ b;
    t = (d << 5) | (d >>> 27);
    c = (t + f + c + 1859775393 + blocks[j + 2]) << 0;
    e = (e << 30) | (e >>> 2);

    f = d ^ e ^ a;
    t = (c << 5) | (c >>> 27);
    b = (t + f + b + 1859775393 + blocks[j + 3]) << 0;
    d = (d << 30) | (d >>> 2);

    f = c ^ d ^ e;
    t = (b << 5) | (b >>> 27);
    a = (t + f + a + 1859775393 + blocks[j + 4]) << 0;
    c = (c << 30) | (c >>> 2);
  }

  for (; j < 60; j += 5) {
    f = (b & c) | (b & d) | (c & d);
    t = (a << 5) | (a >>> 27);
    e = (t + f + e - 1894007588 + blocks[j]) << 0;
    b = (b << 30) | (b >>> 2);

    f = (a & b) | (a & c) | (b & c);
    t = (e << 5) | (e >>> 27);
    d = (t + f + d - 1894007588 + blocks[j + 1]) << 0;
    a = (a << 30) | (a >>> 2);

    f = (e & a) | (e & b) | (a & b);
    t = (d << 5) | (d >>> 27);
    c = (t + f + c - 1894007588 + blocks[j + 2]) << 0;
    e = (e << 30) | (e >>> 2);

    f = (d & e) | (d & a) | (e & a);
    t = (c << 5) | (c >>> 27);
    b = (t + f + b - 1894007588 + blocks[j + 3]) << 0;
    d = (d << 30) | (d >>> 2);

    f = (c & d) | (c & e) | (d & e);
    t = (b << 5) | (b >>> 27);
    a = (t + f + a - 1894007588 + blocks[j + 4]) << 0;
    c = (c << 30) | (c >>> 2);
  }

  for (; j < 80; j += 5) {
    f = b ^ c ^ d;
    t = (a << 5) | (a >>> 27);
    e = (t + f + e - 899497514 + blocks[j]) << 0;
    b = (b << 30) | (b >>> 2);

    f = a ^ b ^ c;
    t = (e << 5) | (e >>> 27);
    d = (t + f + d - 899497514 + blocks[j + 1]) << 0;
    a = (a << 30) | (a >>> 2);

    f = e ^ a ^ b;
    t = (d << 5) | (d >>> 27);
    c = (t + f + c - 899497514 + blocks[j + 2]) << 0;
    e = (e << 30) | (e >>> 2);

    f = d ^ e ^ a;
    t = (c << 5) | (c >>> 27);
    b = (t + f + b - 899497514 + blocks[j + 3]) << 0;
    d = (d << 30) | (d >>> 2);

    f = c ^ d ^ e;
    t = (b << 5) | (b >>> 27);
    a = (t + f + a - 899497514 + blocks[j + 4]) << 0;
    c = (c << 30) | (c >>> 2);
  }

  this.h0 = (this.h0 + a) << 0;
  this.h1 = (this.h1 + b) << 0;
  this.h2 = (this.h2 + c) << 0;
  this.h3 = (this.h3 + d) << 0;
  this.h4 = (this.h4 + e) << 0;
};

Sha1.prototype.hex = function () {
  this.finalize();

  var h0 = this.h0,
    h1 = this.h1,
    h2 = this.h2,
    h3 = this.h3,
    h4 = this.h4;

  return (
    HEX_CHARS[(h0 >>> 28) & 0x0f] +
    HEX_CHARS[(h0 >>> 24) & 0x0f] +
    HEX_CHARS[(h0 >>> 20) & 0x0f] +
    HEX_CHARS[(h0 >>> 16) & 0x0f] +
    HEX_CHARS[(h0 >>> 12) & 0x0f] +
    HEX_CHARS[(h0 >>> 8) & 0x0f] +
    HEX_CHARS[(h0 >>> 4) & 0x0f] +
    HEX_CHARS[h0 & 0x0f] +
    HEX_CHARS[(h1 >>> 28) & 0x0f] +
    HEX_CHARS[(h1 >>> 24) & 0x0f] +
    HEX_CHARS[(h1 >>> 20) & 0x0f] +
    HEX_CHARS[(h1 >>> 16) & 0x0f] +
    HEX_CHARS[(h1 >>> 12) & 0x0f] +
    HEX_CHARS[(h1 >>> 8) & 0x0f] +
    HEX_CHARS[(h1 >>> 4) & 0x0f] +
    HEX_CHARS[h1 & 0x0f] +
    HEX_CHARS[(h2 >>> 28) & 0x0f] +
    HEX_CHARS[(h2 >>> 24) & 0x0f] +
    HEX_CHARS[(h2 >>> 20) & 0x0f] +
    HEX_CHARS[(h2 >>> 16) & 0x0f] +
    HEX_CHARS[(h2 >>> 12) & 0x0f] +
    HEX_CHARS[(h2 >>> 8) & 0x0f] +
    HEX_CHARS[(h2 >>> 4) & 0x0f] +
    HEX_CHARS[h2 & 0x0f] +
    HEX_CHARS[(h3 >>> 28) & 0x0f] +
    HEX_CHARS[(h3 >>> 24) & 0x0f] +
    HEX_CHARS[(h3 >>> 20) & 0x0f] +
    HEX_CHARS[(h3 >>> 16) & 0x0f] +
    HEX_CHARS[(h3 >>> 12) & 0x0f] +
    HEX_CHARS[(h3 >>> 8) & 0x0f] +
    HEX_CHARS[(h3 >>> 4) & 0x0f] +
    HEX_CHARS[h3 & 0x0f] +
    HEX_CHARS[(h4 >>> 28) & 0x0f] +
    HEX_CHARS[(h4 >>> 24) & 0x0f] +
    HEX_CHARS[(h4 >>> 20) & 0x0f] +
    HEX_CHARS[(h4 >>> 16) & 0x0f] +
    HEX_CHARS[(h4 >>> 12) & 0x0f] +
    HEX_CHARS[(h4 >>> 8) & 0x0f] +
    HEX_CHARS[(h4 >>> 4) & 0x0f] +
    HEX_CHARS[h4 & 0x0f]
  );
};

Sha1.prototype.toString = Sha1.prototype.hex;

Sha1.prototype.digest = function () {
  this.finalize();

  var h0 = this.h0,
    h1 = this.h1,
    h2 = this.h2,
    h3 = this.h3,
    h4 = this.h4;

  return [
    (h0 >>> 24) & 0xff,
    (h0 >>> 16) & 0xff,
    (h0 >>> 8) & 0xff,
    h0 & 0xff,
    (h1 >>> 24) & 0xff,
    (h1 >>> 16) & 0xff,
    (h1 >>> 8) & 0xff,
    h1 & 0xff,
    (h2 >>> 24) & 0xff,
    (h2 >>> 16) & 0xff,
    (h2 >>> 8) & 0xff,
    h2 & 0xff,
    (h3 >>> 24) & 0xff,
    (h3 >>> 16) & 0xff,
    (h3 >>> 8) & 0xff,
    h3 & 0xff,
    (h4 >>> 24) & 0xff,
    (h4 >>> 16) & 0xff,
    (h4 >>> 8) & 0xff,
    h4 & 0xff,
  ];
};

Sha1.prototype.array = Sha1.prototype.digest;

Sha1.prototype.arrayBuffer = function () {
  this.finalize();

  var buffer = new ArrayBuffer(20);
  var dataView = new DataView(buffer);
  dataView.setUint32(0, this.h0);
  dataView.setUint32(4, this.h1);
  dataView.setUint32(8, this.h2);
  dataView.setUint32(12, this.h3);
  dataView.setUint32(16, this.h4);
  return buffer;
};

function HmacSha1(key, sharedMemory) {
  var i,
    result = formatMessage(key);
  key = result[0];
  if (result[1]) {
    var bytes = [],
      length = key.length,
      index = 0,
      code;
    for (i = 0; i < length; ++i) {
      code = key.charCodeAt(i);
      if (code < 0x80) {
        bytes[index++] = code;
      } else if (code < 0x800) {
        bytes[index++] = 0xc0 | (code >>> 6);
        bytes[index++] = 0x80 | (code & 0x3f);
      } else if (code < 0xd800 || code >= 0xe000) {
        bytes[index++] = 0xe0 | (code >>> 12);
        bytes[index++] = 0x80 | ((code >>> 6) & 0x3f);
        bytes[index++] = 0x80 | (code & 0x3f);
      } else {
        code = 0x10000 + (((code & 0x3ff) << 10) | (key.charCodeAt(++i) & 0x3ff));
        bytes[index++] = 0xf0 | (code >>> 18);
        bytes[index++] = 0x80 | ((code >>> 12) & 0x3f);
        bytes[index++] = 0x80 | ((code >>> 6) & 0x3f);
        bytes[index++] = 0x80 | (code & 0x3f);
      }
    }
    key = bytes;
  }

  if (key.length > 64) {
    key = new Sha1(true).update(key).array();
  }

  var oKeyPad = [],
    iKeyPad = [];
  for (i = 0; i < 64; ++i) {
    var b = key[i] || 0;
    oKeyPad[i] = 0x5c ^ b;
    iKeyPad[i] = 0x36 ^ b;
  }

  Sha1.call(this, sharedMemory);

  this.update(iKeyPad);
  this.oKeyPad = oKeyPad;
  this.inner = true;
  this.sharedMemory = sharedMemory;
}

HmacSha1.prototype = new Sha1();

HmacSha1.prototype.finalize = function () {
  Sha1.prototype.finalize.call(this);
  if (this.inner) {
    this.inner = false;
    var innerHash = this.array();
    Sha1.call(this, this.sharedMemory);
    this.update(this.oKeyPad);
    this.update(innerHash);
    Sha1.prototype.finalize.call(this);
  }
};

/* 以下是内部实现需要的es模块化导出方法 */
export const sha1 = createMethod();
export const sha1_hmac = createHmacMethod();
